import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    TextField,
    Typography,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useLogout } from 'react-admin';
import { ChangePassword } from '../../common/apiClient';

/**
 * The screen an account sees while it is still using a password somebody else
 * chose for it.
 *
 * WHAT THIS SCREEN IS NOT
 * ----------------------
 * It is not the enforcement. API_Controller::enforce_password_change() is, and
 * it answers 428 to every endpoint but this one. Deleting the flag out of
 * localStorage gets you a working menu in front of an API that refuses to
 * answer -- which is the correct failure, and the reason the gate lives on the
 * server rather than here.
 *
 * WHY IT REPLACES THE WHOLE APPLICATION RATHER THAN OPENING A DIALOG
 * -----------------------------------------------------------------
 * A dialog can be dismissed, and behind it sit lists that would every one fail
 * with 428 and fill the screen with errors that look like a broken product.
 * There is exactly one useful action available to this session, so it is the
 * only one shown.
 *
 * WHY THE CURRENT PASSWORD IS ASKED FOR
 * ------------------------------------
 * It proves the person typing is the one holding the slip. The session alone
 * does not: if a temporary password leaked and somebody else got in first,
 * asking only for a new password would let THEM lock the real owner out --
 * turning a shared password into a stolen account. It also costs nothing here,
 * because the user is looking at the slip as they type.
 */
const ForcePasswordChange = () => {
    const logout = useLogout();

    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    // Mirrors User::password_problem() on the server. Checked here only so the
    // user is told before a round trip; the server never trusts this.
    const localProblem = (): string | null => {
        if (next.length < 8) return 'Your new password must be at least 8 characters long.';
        if (next.length > 72) return 'Your new password must be 72 characters or fewer.';
        if (!/[A-Za-z]/.test(next) || !/[0-9]/.test(next))
            return 'Your new password must have at least one letter and one number.';
        if (next === current) return 'Your new password must be different from your current one.';
        if (next !== confirm) return 'The two new passwords do not match.';
        return null;
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const problem = localProblem();
        if (problem) {
            setError(problem);
            return;
        }

        setBusy(true);
        try {
            const response: any = await ChangePassword({
                existing_password: current,
                new_password: next,
            });

            if (response?.status === 'success') {
                localStorage.removeItem('must_change_password');
                setDone(true);
            } else {
                setError(
                    response?.message || 'The password could not be changed. Please try again.'
                );
            }
        } catch (err: any) {
            setError(err?.message || 'The password could not be changed. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    // Signing in again is not ceremony. The old session was opened with the
    // password that has just been retired, and every OTHER session opened with
    // it has been ended by the server. Coming back in with the new one is how
    // the user finds out it works, while they still remember typing it.
    if (done) {
        return (
            <Shell>
                <Alert severity="success" sx={{ mb: 2 }}>
                    Your password has been changed. Nobody else knows it now.
                </Alert>
                <Typography variant="body2" sx={{ mb: 3 }} color="text.secondary">
                    Please sign in again with your new password.
                </Typography>
                <Button variant="contained" fullWidth onClick={() => logout()}>
                    Sign in again
                </Button>
            </Shell>
        );
    }

    return (
        <Shell>
            <Alert severity="warning" sx={{ mb: 2 }}>
                You are still using the temporary password you were given.
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Somebody else chose that password, and it may have been printed or shared. Please
                set your own before you go any further.
            </Typography>

            <form onSubmit={submit}>
                <TextField
                    label="Temporary password"
                    type="password"
                    value={current}
                    onChange={e => setCurrent(e.target.value)}
                    fullWidth
                    required
                    autoFocus
                    margin="normal"
                    autoComplete="current-password"
                    helperText="The one you were given."
                />
                <TextField
                    label="New password"
                    type="password"
                    value={next}
                    onChange={e => setNext(e.target.value)}
                    fullWidth
                    required
                    margin="normal"
                    autoComplete="new-password"
                    helperText="At least 8 characters, with a letter and a number."
                />
                <TextField
                    label="New password again"
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    fullWidth
                    required
                    margin="normal"
                    autoComplete="new-password"
                />

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={busy}
                    sx={{ mt: 3 }}
                    startIcon={busy ? <CircularProgress size={16} /> : <LockResetIcon />}
                >
                    {busy ? 'Saving...' : 'Set my password'}
                </Button>
            </form>

            <Divider sx={{ my: 3 }} />

            <Button size="small" fullWidth onClick={() => logout()}>
                Sign out instead
            </Button>
        </Shell>
    );
};

const Shell = ({ children }: { children: React.ReactNode }) => (
    <Box
        sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            p: 2,
        }}
    >
        <Card sx={{ width: '100%', maxWidth: 440 }}>
            <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Set your password
                </Typography>
                {children}
            </CardContent>
        </Card>
    </Box>
);

export default ForcePasswordChange;
